import { fg } from '@atlaskit/platform-feature-flags';

import { type EditionAwarenessContextType } from './edition-awareness-context';

/**
 * The near-limit storage threshold (as a fraction of the storage limit).
 *
 * Product-chosen v1 value derived from the I12 spike (#349428). The PRD
 * (JSMGROWTH-267, R1) requires that this stay aligned with the AdminHub and
 * JSM storage management near-limit thresholds, but does not specify the exact
 * percentage. There is currently no shared source of truth — AdminHub and JSM
 * storage management each define their own constant.
 *
 * If you change this value, you MUST also update:
 *   - AdminHub storage management near-limit threshold
 *   - JSM storage management page warning threshold
 * and confirm with the JSMGROWTH-267 product owner before doing so.
 *
 * TODO(JSMGROWTH-267 follow-up): replace this local constant with a shared
 * constant exported from a single source (e.g. an EA-storage-thresholds
 * package) once AdminHub and JSM storage management agree on a host.
 *
 * Gated behind `jsmgrowth-267_-_enforce_storage_limits` feature gate.
 */
export const STORAGE_NEAR_LIMIT_THRESHOLD = 0.9;

// Hard-limit and near-limit are mutually exclusive at the threshold of
// storageUsageInGB === storageLimitInGB. The hard-limit predicate uses
// `>=` so we must use a strict `<` here AND defensively guard against
// floating-point drift around the boundary (e.g. usage 0.999_999 of a
// 1.0 limit). Without this, a fractional gigabyte of drift would route
// the user to "Storage almost full" while AdminHub shows "full".
//
// We treat anything within 1e-9 of the limit as already at the hard
// limit. JSM storage usage is reported in GB with at most 6 decimal
// places of precision upstream, so 1e-9 is well below any real-world
// drift while still keeping the boundary deterministic.
const FLOAT_DRIFT_TOLERANCE = 1e-9;

export function isEligibleForJsmStorageNearLimit(context: EditionAwarenessContextType): boolean {
	const {
		storageUsageInGB,
		storageLimitInGB,
		storageIsUnlimited,
		product,
		edition,
		canUpgrade,
		canDirectBuyUpgrade,
	} = context;

	if (!storageUsageInGB || !storageLimitInGB) {
		return false;
	}

	const usageRatio = storageUsageInGB / storageLimitInGB;

	const isStrictlyBelowHardLimit = usageRatio < 1 - FLOAT_DRIFT_TOLERANCE;

	return (
		product === 'jira-service-management' &&
		edition === 'free' &&
		!!(canUpgrade || canDirectBuyUpgrade) &&
		!storageIsUnlimited &&
		usageRatio >= STORAGE_NEAR_LIMIT_THRESHOLD &&
		isStrictlyBelowHardLimit &&
		fg('jsmgrowth-267_-_enforce_storage_limits')
	);
}
